import React, { useState } from 'react';
import { products, districts } from '../../data/content';
import { calculateDeliveryPrice } from '../../utils/deliveryUtils';
import './NewOrderModal.css';

const NewOrderModal = ({ isOpen, onClose, onSubmit }) => {
  const deliveryTypes = ['Home Delivery', 'Pickup'];
  const defaultDeliveryDate = new Date();
  defaultDeliveryDate.setDate(defaultDeliveryDate.getDate() + 2);

  const [formData, setFormData] = useState({
    customerName: '',
    phone1: '',
    phone2: '',
    deliveryAddress: '',
    area: districts[0] || 'Colombo',
    deliveryType: 'Home Delivery',
    deliveryDate: defaultDeliveryDate.toISOString().split('T')[0],
    specialInstructions: '',
    paymentMethod: 'Cash on Delivery',
    status: 'Pending',
  });

  const [items, setItems] = useState([]);
  const [itemInput, setItemInput] = useState({ packageId: '', quantity: '' });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.customerName.trim()) newErrors.customerName = 'Customer name is required';
    if (!formData.phone1.trim()) newErrors.phone1 = 'Phone number is required';
    if (!formData.deliveryAddress.trim()) newErrors.deliveryAddress = 'Delivery address is required';
    if (!formData.area) newErrors.area = 'Delivery area is required';
    if (items.length === 0) newErrors.items = 'At least one item is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const handleAddItem = () => {
    if (!itemInput.packageId || !itemInput.quantity) {
      setErrors({ ...errors, itemInput: 'Please select a package and enter quantity' });
      return;
    }

    const selectedProduct = products.find(p => p.id === parseInt(itemInput.packageId));
    
    if (!selectedProduct || selectedProduct.price === 0) {
      setErrors({ ...errors, itemInput: 'Invalid package selected or product not available' });
      return;
    }

    const newItem = {
      id: Date.now(),
      productId: selectedProduct.id,
      description: selectedProduct.title,
      quantity: parseInt(itemInput.quantity),
      price: selectedProduct.price,
    };

    setItems([...items, newItem]);
    setItemInput({ packageId: '', quantity: '' });
    setErrors({ ...errors, itemInput: '' });
  };

  const handleRemoveItem = (id) => {
    setItems(items.filter((item) => item.id !== id));
  };

  // Helper function to calculate delivery charge
  const getDeliveryCharge = () => {
    // Convert items to the format expected by calculateDeliveryPrice
    const cartItems = items.map(item => ({
      id: item.productId,
      quantity: item.quantity,
    }));
    return calculateDeliveryPrice(cartItems);
  };

  const getSubtotal = () => {
    return items.reduce((sum, item) => sum + item.quantity * item.price, 0);
  };

  const getTotalWithDelivery = () => {
    return getSubtotal() + getDeliveryCharge();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const subtotal = items.reduce((sum, item) => sum + item.quantity * item.price, 0);
    const deliveryCharge = getDeliveryCharge();

    const orderData = {
      customerName: formData.customerName,
      email: '',
      phone1: formData.phone1,
      phone2: formData.phone2 || '',
      deliveryAddress: formData.deliveryAddress,
      address: formData.deliveryAddress, // API expects "address" not "deliveryAddress"
      area: formData.area,
      deliveryType: formData.deliveryType,
      paymentMethod: formData.paymentMethod,
      deliveryDate: new Date(formData.deliveryDate).toISOString(),
      specialInstructions: formData.specialInstructions || '',
      subtotal: subtotal,
      deliveryCharge: deliveryCharge,
      totalPrice: subtotal + deliveryCharge,
      items: items.map((item) => ({
        productId: item.productId,
        productName: item.description,
        quantity: item.quantity,
        price: parseFloat(item.price),
      })),
    };

    try {
      await onSubmit(orderData);
    } catch (err) {
      // Error is handled by parent component
    }
  };

  const handleClose = () => {
    setFormData({
      customerName: '',
      phone1: '',
      phone2: '',
      deliveryAddress: '',
      area: districts[0] || 'Colombo',
      deliveryType: 'Home Delivery',
      deliveryDate: new Date().toISOString().split('T')[0],
      specialInstructions: '',
      paymentMethod: 'Cash on Delivery',
      status: 'Pending',
    });
    setItems([]);
    setItemInput({ packageId: '', quantity: '' });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>Create New Order</h2>
          <button className="modal-close" onClick={handleClose}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="new-order-form">
          {/* Customer Details Section */}
          <div className="form-section">
            <h3>Customer Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="customerName">
                  Customer Name <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="customerName"
                  name="customerName"
                  value={formData.customerName}
                  onChange={handleInputChange}
                  placeholder="Enter customer name"
                  className={errors.customerName ? 'error' : ''}
                />
                {errors.customerName && <span className="error-text">{errors.customerName}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phone1">
                  Phone 1 <span className="required">*</span>
                </label>
                <input
                  type="tel"
                  id="phone1"
                  name="phone1"
                  value={formData.phone1}
                  onChange={handleInputChange}
                  placeholder="Enter primary phone"
                  className={errors.phone1 ? 'error' : ''}
                />
                {errors.phone1 && <span className="error-text">{errors.phone1}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="phone2">Phone 2</label>
                <input
                  type="tel"
                  id="phone2"
                  name="phone2"
                  value={formData.phone2}
                  onChange={handleInputChange}
                  placeholder="Enter secondary phone"
                />
              </div>
            </div>
          </div>

          {/* Delivery Details Section */}
          <div className="form-section">
            <h3>Delivery Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="deliveryAddress">
                  Delivery Address <span className="required">*</span>
                </label>
                <textarea
                  id="deliveryAddress"
                  name="deliveryAddress"
                  value={formData.deliveryAddress}
                  onChange={handleInputChange}
                  placeholder="Enter full delivery address"
                  rows="3"
                  className={errors.deliveryAddress ? 'error' : ''}
                />
                {errors.deliveryAddress && (
                  <span className="error-text">{errors.deliveryAddress}</span>
                )}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="deliveryType">Delivery Type</label>
                <select
                  id="deliveryType"
                  name="deliveryType"
                  value={formData.deliveryType}
                  onChange={handleInputChange}
                >
                  {deliveryTypes.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="area">
                  Delivery Area <span className="required">*</span>
                </label>
                <select
                  id="area"
                  name="area"
                  value={formData.area}
                  onChange={handleInputChange}
                  className={errors.area ? 'error' : ''}
                >
                  {districts.map((areaOption) => (
                    <option key={areaOption} value={areaOption}>
                      {areaOption}
                    </option>
                  ))}
                </select>
                {errors.area && <span className="error-text">{errors.area}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="deliveryDate">Delivery Date</label>
                <input
                  type="date"
                  id="deliveryDate"
                  name="deliveryDate"
                  value={formData.deliveryDate}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  <option value="Pending">Pending</option>
                  <option value="Processing">Processing</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>
            </div>
          </div>

          {/* Payment Method Section */}
          <div className="form-section">
            <h3>Payment Method</h3>
            <div className="payment-options">
              <label className="payment-option">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="Cash on Delivery"
                  checked={formData.paymentMethod === 'Cash on Delivery'}
                  onChange={handleInputChange}
                />
                <span className="radio-label"><span>💵</span> Cash on Delivery</span>
              </label>
              <label className="payment-option">
                <input
                  type="radio"
                  name="paymentMethod"
                  value="Card Payment"
                  checked={formData.paymentMethod === 'Card Payment'}
                  onChange={handleInputChange}
                />
                <span className="radio-label"><span>💳</span> Card Payment</span>
              </label>
            </div>
          </div>

          {/* Order Items Section */}
          <div className="form-section">
            <h3>Order Items</h3>

            <div className="items-input-section">
              <div className="items-inline-row">
                <div className="form-group">
                  <label htmlFor="packageSelect">Select Package</label>
                  <select
                    id="packageSelect"
                    value={itemInput.packageId}
                    onChange={(e) => setItemInput({ ...itemInput, packageId: e.target.value })}
                  >
                    <option value="">-- Choose a Package --</option>
                    {products.map((product) =>
                      product.price > 0 ? (
                        <option key={product.id} value={product.id}>
                          {product.title} - Rs. {product.price.toFixed(2)}
                        </option>
                      ) : null
                    )}
                  </select>
                </div>

                <div className="form-group quantity-group">
                  <label htmlFor="itemQuantity">Quantity</label>
                  <input
                    type="number"
                    id="itemQuantity"
                    value={itemInput.quantity}
                    onChange={(e) => setItemInput({ ...itemInput, quantity: e.target.value })}
                    placeholder="1"
                    min="1"
                  />
                </div>

                <div className="form-group btn-group">
                  <label>&nbsp;</label>
                  <button type="button" className="btn-add-item" onClick={handleAddItem}>
                    Add to Order
                  </button>
                </div>
              </div>
              {errors.itemInput && <span className="error-text">{errors.itemInput}</span>}
            </div>

            {items.length > 0 ? (
              <div className="items-list">
                <table>
                  <thead>
                    <tr>
                      <th>Package</th>
                      <th>Quantity</th>
                      <th>Unit Price</th>
                      <th>Total</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item) => (
                      <tr key={item.id}>
                        <td>{item.description}</td>
                        <td>{item.quantity}</td>
                        <td>Rs. {item.price.toFixed(2)}</td>
                        <td>Rs. {(item.quantity * item.price).toFixed(2)}</td>
                        <td>
                          <button
                            type="button"
                            className="btn-remove-item"
                            onClick={() => handleRemoveItem(item.id)}
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="items-total">
                  <div className="total-breakdown">
                    <div className="total-row">
                      <span>Subtotal:</span>
                      <span>Rs. {getSubtotal().toFixed(2)}</span>
                    </div>
                    <div className="total-row">
                      <span>Delivery Charge:</span>
                      <span className="delivery-amount">Rs. {getDeliveryCharge().toFixed(2)}</span>
                    </div>
                    <div className="total-row total-final">
                      <strong>Order Total:</strong>
                      <strong>Rs. {getTotalWithDelivery().toFixed(2)}</strong>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="no-items-message">
                {errors.items ? (
                  <span className="error-text">{errors.items}</span>
                ) : (
                  <p>No items added yet. Add items to create the order.</p>
                )}
              </div>
            )}
          </div>

          {/* Special Instructions Section */}
          <div className="form-section">
            <div className="form-group">
              <label htmlFor="specialInstructions">Special Instructions</label>
              <textarea
                id="specialInstructions"
                name="specialInstructions"
                value={formData.specialInstructions}
                onChange={handleInputChange}
                placeholder="Any special handling or instructions..."
                rows="2"
              />
            </div>
          </div>

          {/* Form Actions */}
          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={handleClose}>
              Cancel
            </button>
            <button type="submit" className="btn-create">
              Create Order
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewOrderModal;
